import type { NotificationType } from './types/notifications.types';

/** A human label + one-line description per notification type, for the settings page rows. */
export interface NotificationTypeMeta {
  label: string;
  description: string;
}

/**
 * Copy for every notification type the settings page renders. Keyed by the wire enum value so a new
 * backend type surfaces here as a single entry (and a missing one falls back to a readable default).
 *
 * There is exactly one entry per backend `NotificationType`, and the backend only defines types it
 * actually raises. Eight entries were removed alongside their enum values: six belonged to modules that
 * are not built (Managed Workforce, Billing, Vendor, Training), `SYSTEM_ANNOUNCEMENT` had no authoring
 * surface, and `OFFER_RECEIVED` had no producer at all. Together they meant a user could carefully
 * configure eight toggles that did nothing.
 *
 * A description must say what the row GOVERNS, in the recipient's terms — this page is the only place a
 * user learns what they are switching off, so a vague label becomes a support ticket. Never describe
 * coverage a type does not have.
 */
const NOTIFICATION_TYPE_META: Record<NotificationType, NotificationTypeMeta> = {
  // Covers submission, shortlisting, rejection and hiring. It does NOT cover an offer being extended —
  // nothing notifies on that today — so the copy deliberately does not promise offers.
  APPLICATION_STATUS_CHANGED: {
    label: 'Application updates',
    description: 'When one of your job applications is shortlisted, rejected, or you are hired.',
  },
  INTERVIEW_SCHEDULED: {
    label: 'Interview updates',
    description: 'When an interview is scheduled, moved, cancelled, or marked complete.',
  },
  INTERVIEW_REMINDER: {
    label: 'Interview reminders',
    description: 'A reminder shortly before an upcoming interview.',
  },
  // Addressed to the person who published the job, as a confirmation. It is not a candidate job alert —
  // there is no job-alert feature — so the copy must not imply one.
  JOB_POSTED: {
    label: 'Your job postings',
    description: 'When a job you published goes live.',
  },
  // Mandatory: rendered locked-on with a "Required" chip and no usable toggle.
  COMPANY_INVITATION_RECEIVED: {
    label: 'Company invitations',
    description: 'When a company invites you to join their workspace. Always on.',
  },
  // Mandatory, for the same reason: it reports a change to the recipient's own access.
  COMPANY_MEMBERSHIP_CHANGED: {
    label: 'Your company access',
    description: 'When you join a company workspace or your access changes. Always on.',
  },
  // This row is load-bearing: it is the switch a candidate uses to stop hearing from recruiters at
  // all (ADR-0023 §4), so it has to say what it governs rather than fall back to a humanised enum name.
  CANDIDATE_INVITATION_RECEIVED: {
    label: 'Invitations to apply',
    description: 'When a company invites you to apply for one of their jobs.',
  },
  CANDIDATE_INVITATION_RESPONDED: {
    label: 'Replies to your invitations',
    description: 'When a candidate you invited to apply accepts, declines, or lets the invitation expire.',
  },
  COMPANY_INVITATION_EXPIRING_SOON: {
    label: 'Invitations expiring soon',
    description: 'When a company invitation you sent is about to expire unanswered.',
  },
  APPLICATION_STALLED: {
    label: 'Stalled applications',
    description: 'When an application assigned to you has had no activity for a while.',
  },
};

/** Copy for a type not present in the map — keeps the page resilient to a new backend enum value. */
export function metaFor(type: NotificationType): NotificationTypeMeta {
  return (
    NOTIFICATION_TYPE_META[type] ?? {
      label: type
        .toLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
      description: '',
    }
  );
}
